import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useOutboxStore } from '@/features/offline/model/outboxStore'
import type { CalendarEvent } from '@/entities/calendar/types'
import { AppError } from '@/shared/lib/appError'
import { createId } from '@/shared/lib/id'
import { getErrorMessage, isLikelyOfflineError } from '@/shared/lib/network'
import { indexedDBJSON } from '@/shared/lib/persistence'
import { logClientError } from '@/shared/lib/safeClientLog'
import { eventsRepository } from '@/shared/repositories/events/eventsRepository'

type NewCalendarEvent = Omit<CalendarEvent, 'id'>

interface CalendarStoreState {
  events: CalendarEvent[]
  loaded: boolean
  fetchEvents: () => Promise<void>
  addEvent: (event: NewCalendarEvent) => CalendarEvent | null
  updateEvent: (eventId: string, event: Partial<NewCalendarEvent>) => Promise<void>
  removeEvent: (eventId: string) => Promise<void>
  getUpcomingEvents: (limit?: number) => CalendarEvent[]
}

const sortByStartDate = (events: CalendarEvent[]): CalendarEvent[] =>
  [...events].sort(
    (left, right) =>
      new Date(left.startDateTime).getTime() - new Date(right.startDateTime).getTime(),
  )

/** IDs de eventos otimistas ainda não persistidos. Evita que fetchEvents sobrescreva e os remova. */
const pendingOptimisticIds = new Set<string>()

const apiToEvent = (e: {
  id: string
  title: string
  description?: string
  location?: string
  locationId?: string | null
  startDateTime: string
  endDateTime: string
  allDay: boolean
  patientId?: string
  reminderEnabled?: boolean
  reminderMinutesBefore?: number
  bookingStatus?: import('@/entities/calendar/types').BookingStatus
}): CalendarEvent => ({
  id: e.id,
  title: e.title,
  description: e.description,
  location: e.location,
  locationId: e.locationId,
  startDateTime: e.startDateTime,
  endDateTime: e.endDateTime,
  allDay: e.allDay,
  patientId: e.patientId,
  reminderEnabled: e.reminderEnabled,
  reminderMinutesBefore: e.reminderMinutesBefore,
  bookingStatus: e.bookingStatus,
})

export const useCalendarStore = create<CalendarStoreState>()(
  persist(
    (set, get) => ({
      events: [],
      loaded: false,

      fetchEvents: async () => {
        try {
          const { events } = await eventsRepository.list()
          const apiEvents = sortByStartDate(events.map(apiToEvent))
          set((state) => {
            const pending = state.events.filter((e) => pendingOptimisticIds.has(e.id))
            const merged = [...apiEvents]
            for (const p of pending) {
              const sameInApi = apiEvents.some(
                (a) =>
                  a.title === p.title &&
                  a.startDateTime === p.startDateTime &&
                  a.endDateTime === p.endDateTime,
              )
              if (sameInApi) {
                pendingOptimisticIds.delete(p.id)
              } else {
                merged.push(p)
              }
            }
            return { events: sortByStartDate(merged), loaded: true }
          })
        } catch (error) {
          logClientError('[login] fetchEvents: falha ao carregar', error)
          /* keep local state */
        }
      },

      addEvent: (event) => {
        const title = event.title.trim()
        if (!title) return null

        const startDate = new Date(event.startDateTime)
        const endDate = new Date(event.endDateTime)
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null

        const normalizedStart = startDate.toISOString()
        const normalizedEnd = endDate.toISOString()
        if (new Date(normalizedEnd).getTime() <= new Date(normalizedStart).getTime()) return null

        const newEvent: CalendarEvent = {
          id: createId(),
          title,
          description: event.description?.trim() || undefined,
          location: event.location?.trim() || undefined,
          locationId: event.locationId ?? undefined,
          startDateTime: normalizedStart,
          endDateTime: normalizedEnd,
          allDay: event.allDay ?? false,
          patientId: event.patientId,
          reminderEnabled: event.reminderEnabled,
          reminderMinutesBefore: event.reminderMinutesBefore,
        }

        pendingOptimisticIds.add(newEvent.id)
        set((state) => ({
          events: sortByStartDate([...state.events, newEvent]),
        }))

        const repoPayload = {
          title: newEvent.title,
          description: newEvent.description,
          location: newEvent.location,
          locationId: newEvent.locationId,
          startDateTime: newEvent.startDateTime,
          endDateTime: newEvent.endDateTime,
          allDay: newEvent.allDay ?? false,
          patientId: newEvent.patientId,
          reminderEnabled: newEvent.reminderEnabled,
          reminderMinutesBefore: newEvent.reminderMinutesBefore,
        }

        eventsRepository
          .create(repoPayload)
          .then(({ event }) => {
            pendingOptimisticIds.delete(newEvent.id)
            const persisted = apiToEvent(event)
            set((state) => {
              const withoutOptimistic = state.events.filter((item) => item.id !== newEvent.id)
              const deduped = withoutOptimistic.filter((item) => item.id !== persisted.id)
              return { events: sortByStartDate([...deduped, persisted]) }
            })
          })
          .catch((error) => {
            if (!isLikelyOfflineError(error)) {
              pendingOptimisticIds.delete(newEvent.id)
              return
            }
            useOutboxStore.getState().enqueue(
              'event.create',
              repoPayload,
              getErrorMessage(error, 'Falha de conexão ao sincronizar evento.'),
            )
          })

        return newEvent
      },

      updateEvent: async (eventId, payload) => {
        const title = payload.title?.trim()
        if (payload.title !== undefined && !title) {
          throw new AppError('Informe o título do evento.', { code: 'VALIDATION', status: 422 })
        }

        const previous = get().events
        const existing = previous.find((e) => e.id === eventId)
        if (!existing) {
          throw new AppError('Evento não encontrado.', { code: 'NOT_FOUND', status: 404 })
        }

        // Normaliza para ISO UTC (mesmo tratamento de addEvent) para que o estado
        // otimista nunca fique com um formato diferente do que o servidor retorna —
        // evita drift entre "YYYY-MM-DDTHH:mm" (datetime-local cru) e ISO com timezone.
        let normalizedStart: string | undefined
        if (payload.startDateTime !== undefined) {
          const startDate = new Date(payload.startDateTime)
          if (Number.isNaN(startDate.getTime())) {
            throw new AppError('Horário de início inválido.', { code: 'VALIDATION', status: 422 })
          }
          normalizedStart = startDate.toISOString()
        }

        let normalizedEnd: string | undefined
        if (payload.endDateTime !== undefined) {
          const endDate = new Date(payload.endDateTime)
          if (Number.isNaN(endDate.getTime())) {
            throw new AppError('Horário de término inválido.', { code: 'VALIDATION', status: 422 })
          }
          normalizedEnd = endDate.toISOString()
        }

        const merged: CalendarEvent = {
          ...existing,
          ...(payload.title !== undefined && { title: payload.title.trim() }),
          ...(payload.description !== undefined && {
            description: payload.description?.trim() || undefined,
          }),
          ...(payload.location !== undefined && {
            location: payload.location?.trim() || undefined,
          }),
          ...(payload.locationId !== undefined && {
            locationId: payload.locationId ?? null,
          }),
          ...(normalizedStart !== undefined && { startDateTime: normalizedStart }),
          ...(normalizedEnd !== undefined && { endDateTime: normalizedEnd }),
          ...(payload.allDay !== undefined && { allDay: payload.allDay }),
          ...(payload.patientId !== undefined && { patientId: payload.patientId }),
          ...(payload.reminderEnabled !== undefined && { reminderEnabled: payload.reminderEnabled }),
          ...(payload.reminderMinutesBefore !== undefined && {
            reminderMinutesBefore: payload.reminderMinutesBefore,
          }),
          ...(payload.bookingStatus !== undefined && { bookingStatus: payload.bookingStatus }),
        }

        if (new Date(merged.endDateTime).getTime() <= new Date(merged.startDateTime).getTime()) {
          throw new AppError('O horário de término deve ser depois do horário de início.', {
            code: 'VALIDATION',
            status: 422,
          })
        }

        set((state) => ({
          events: sortByStartDate(
            state.events.map((e) => (e.id === eventId ? merged : e)),
          ),
        }))
        try {
          await eventsRepository.update(eventId, {
            title: merged.title,
            description: merged.description,
            location: merged.location,
            locationId: merged.locationId,
            startDateTime: merged.startDateTime,
            endDateTime: merged.endDateTime,
            allDay: merged.allDay,
            patientId: merged.patientId,
            reminderEnabled: merged.reminderEnabled,
            reminderMinutesBefore: merged.reminderMinutesBefore,
            bookingStatus: merged.bookingStatus,
          })
        } catch (error) {
          set({ events: previous })
          throw error
        }
      },

      removeEvent: async (eventId) => {
        const previous = get().events
        set((state) => ({
          events: state.events.filter((e) => e.id !== eventId),
        }))
        try {
          await eventsRepository.delete(eventId)
        } catch (error) {
          set({ events: previous })
          throw error
        }
      },

      getUpcomingEvents: (limit = 8) => {
        const now = Date.now()
        const oneHourAgo = now - 60 * 60 * 1000
        return sortByStartDate(
          get().events.filter(
            (event) => new Date(event.endDateTime).getTime() >= oneHourAgo,
          ),
        ).slice(0, limit)
      },
    }),
    {
      name: 'synap-calendar-store',
      storage: indexedDBJSON,
      partialize: (state) => ({ events: state.events }),
      skipHydration: true,
    },
  ),
)
